import moment from 'moment';

import { DateTime, NewsRawDataType, NewsType } from '../../../api/types/apiGetReport';

/* -------------------------------------------------------------------------- */
/* Severity                                                                    */
/* -------------------------------------------------------------------------- */

export type SeverityKey = 'critical' | 'high' | 'medium' | 'low' | 'unknown';

const SEVERITY_RANK: Record<SeverityKey, number> = {
	critical: 0,
	high: 1,
	medium: 2,
	low: 3,
	unknown: 4,
};

export const SEVERITY_LABEL: Record<SeverityKey, string> = {
	critical: 'Critical',
	high: 'High',
	medium: 'Medium',
	low: 'Low',
	unknown: '',
};

export function normalizeSeverity(severity?: string | null): SeverityKey {
	const value = (severity || '').trim().toLowerCase();
	if (value === 'critical' || value === 'high' || value === 'medium' || value === 'low') {
		return value;
	}
	return 'unknown';
}

export function severityRank(severity: SeverityKey): number {
	return SEVERITY_RANK[severity];
}

/* -------------------------------------------------------------------------- */
/* Categories                                                                  */
/* -------------------------------------------------------------------------- */

type CategoryMeta = {
	label: string;
	icon: string;
	/** Distinct hue so the category row carries scanning value (§10.19). */
	color: string;
};

const CATEGORY_META: Record<string, CategoryMeta> = {
	weather_natural_hazard: {
		label: 'Weather & natural hazards',
		icon: 'thermostat',
		color: '#0ea5e9',
	},
	natural_hazard: { label: 'Natural hazards', icon: 'landslide', color: '#0ea5e9' },
	logistics_transportation: {
		label: 'Transportation & logistics',
		icon: 'train',
		color: '#6366f1',
	},
	transportation: { label: 'Transportation', icon: 'train', color: '#6366f1' },
	health_medical: { label: 'Health & medical', icon: 'medication', color: '#10b981' },
	health: { label: 'Health', icon: 'medication', color: '#10b981' },
	safety_security: { label: 'Safety & security', icon: 'shield', color: '#ef4444' },
	security: { label: 'Security', icon: 'shield', color: '#ef4444' },
	crime: { label: 'Crime', icon: 'gavel', color: '#b91c1c' },
	civil_unrest: { label: 'Civil unrest', icon: 'groups', color: '#f97316' },
	political: { label: 'Politics & governance', icon: 'account_balance', color: '#8b5cf6' },
	labor_strike: { label: 'Strikes & labor action', icon: 'engineering', color: '#d97706' },
	infrastructure_utilities: {
		label: 'Infrastructure & utilities',
		icon: 'construction',
		color: '#78716c',
	},
	events_gatherings: { label: 'Events & gatherings', icon: 'celebration', color: '#ec4899' },
	entry_requirements: { label: 'Entry & documents', icon: 'description', color: '#0d9488' },
	travel_advisory: { label: 'Travel advisories', icon: 'campaign', color: '#7c3aed' },
	other: { label: 'Other', icon: 'label', color: '#64748b' },
};

const FALLBACK_HUES = ['#0ea5e9', '#6366f1', '#10b981', '#f97316', '#ec4899', '#7c3aed', '#0d9488'];

function hashString(value: string): number {
	let hash = 0;
	for (let i = 0; i < value.length; i += 1) {
		hash = (hash * 31 + value.charCodeAt(i)) % 100000;
	}
	return hash;
}

/** Turns `weather_natural_hazard` into `Weather natural hazard` for unmapped enums. */
function titleize(value: string): string {
	const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
	if (!words) {
		return 'Other';
	}
	return words.charAt(0).toUpperCase() + words.slice(1);
}

export function categoryMeta(category?: string | null): CategoryMeta {
	const key = (category || '').trim().toLowerCase();
	if (CATEGORY_META[key]) {
		return CATEGORY_META[key];
	}
	return {
		label: titleize(key),
		icon: 'label',
		color: FALLBACK_HUES[hashString(key) % FALLBACK_HUES.length],
	};
}

export function categoryLabel(category?: string | null): string {
	return categoryMeta(category).label;
}

/* -------------------------------------------------------------------------- */
/* Sources                                                                     */
/* -------------------------------------------------------------------------- */

export type SourceTier = 'official' | 'media' | 'social';

export const SOURCE_TIER_LABEL: Record<SourceTier, string> = {
	official: 'Official source',
	media: 'Established media',
	social: 'Social media',
};

const SOCIAL_HOSTS = [
	'instagram.com',
	'facebook.com',
	'fb.com',
	'x.com',
	'twitter.com',
	'tiktok.com',
	'youtube.com',
	'youtu.be',
	'reddit.com',
	't.me',
	'telegram.me',
	'threads.net',
];

const OFFICIAL_HOST_HINTS = [
	'protezionecivile',
	'trenitalia',
	'italiarail',
	'comune.',
	'regione.',
	'ministero',
	'police',
	'polizia',
	'prefettura',
	'aeroporto',
	'airport',
	'transit',
	'metro',
	'ingv',
	'who.int',
	'cdc.gov',
	'europa.eu',
	'un.org',
];

const OFFICIAL_TLDS = ['.gov', '.gov.uk', '.gov.it', '.mil', '.int', '.gob', '.gouv.fr', '.go.jp'];

export function sourceHost(url?: string | null): string {
	if (!url) {
		return '';
	}
	try {
		const { hostname } = new URL(url);
		return hostname.replace(/^www\./i, '');
	} catch {
		const match = url.match(/^(?:https?:\/\/)?([^/?#]+)/i);
		return match ? match[1].replace(/^www\./i, '') : '';
	}
}

export function sourceTier(url?: string | null): SourceTier {
	const host = sourceHost(url).toLowerCase();
	if (!host) {
		return 'media';
	}
	if (SOCIAL_HOSTS.some((social) => host === social || host.endsWith(`.${social}`))) {
		return 'social';
	}
	if (OFFICIAL_TLDS.some((tld) => host.endsWith(tld) || host.includes(`${tld}.`))) {
		return 'official';
	}
	if (OFFICIAL_HOST_HINTS.some((hint) => host.includes(hint))) {
		return 'official';
	}
	return 'media';
}

export function faviconUrl(url?: string | null): string {
	const host = sourceHost(url);
	return host ? `https://www.google.com/s2/favicons?sz=32&domain=${host}` : '';
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                       */
/* -------------------------------------------------------------------------- */

export function dateTimeToDate(value?: DateTime | null): Date | null {
	if (!value || typeof value.year !== 'number') {
		return null;
	}
	return new Date(
		value.year,
		(value.monthValue || 1) - 1,
		value.dayOfMonth || 1,
		value.hour || 0,
		value.minute || 0,
		value.second || 0,
	);
}

const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');

/**
 * Travelers think in local time only (§10.17): rewrite ISO dates as "Aug 3",
 * drop UTC clock readings when a local equivalent is quoted alongside them,
 * and label bare local times as "local".
 */
export function humanizeEventDate(raw?: string | null): string {
	if (!raw) {
		return '';
	}
	let text = raw.trim();

	// "08:00 UTC (10:00 CEST)" -> "10:00 local"
	text = text.replace(
		/\b\d{1,2}:\d{2}\s*(?:UTC|GMT|Z)\b\s*\(\s*(\d{1,2}:\d{2})\s*[A-Z]{2,5}\s*\)/gi,
		'$1 local',
	);
	// "10:00 CEST" -> "10:00 local"
	text = text.replace(/\b(\d{1,2}:\d{2})\s*(?:CEST|CET|EST|EDT|PST|PDT|BST|IST)\b/g, '$1 local');
	// "2026-08-03" -> "Aug 3"
	text = text.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_match, _year, month, day) => {
		const monthIndex = Number(month) - 1;
		const name = MONTHS[monthIndex] || '';
		return name ? `${name} ${Number(day)}` : _match;
	});
	// Drop a dangling ISO time separator left behind by the rewrite above.
	text = text.replace(/\bT(\d{2}:\d{2})(?::\d{2})?\b/g, ' $1');
	text = text.replace(/\s{2,}/g, ' ').trim();

	if (/^ongoing$/i.test(text)) {
		return 'Ongoing';
	}
	return text;
}

export function formatCardDate(item: NewsType, raw: NewsRawDataType | null): string {
	if (raw?.event_date) {
		return humanizeEventDate(raw.event_date);
	}
	const published = dateTimeToDate(item.publishedAt);
	return published ? moment(published).format('MMM D, h:mm A [local]') : '';
}

/* -------------------------------------------------------------------------- */
/* Raw payload                                                                 */
/* -------------------------------------------------------------------------- */

export function parseNewsRawData(raw: string | null): NewsRawDataType | null {
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw) as NewsRawDataType;
	} catch {
		return null;
	}
}

/* -------------------------------------------------------------------------- */
/* Finding model                                                               */
/* -------------------------------------------------------------------------- */

export type Finding = {
	item: NewsType;
	raw: NewsRawDataType | null;
	id: number;
	title: string;
	summary: string;
	body: string;
	category: string;
	severity: SeverityKey;
	sourceUrl: string;
	publishedAt: Date | null;
	eventDateLabel: string;
	affectedAreas: string[];
	operationalImpact: string;
	specificAction: string | null;
	resolved: boolean;
	tokens: Set<string>;
	/** Extra sources folded in by near-duplicate merging (§10.13). */
	mergedSources: { url: string; host: string; tier: SourceTier }[];
	isNew: boolean;
};

const RESOLVED_PATTERN =
	/\b(reopen(?:ed|s)?|resum(?:ed|es)|restor(?:ed|es)|lift(?:ed|s)|resolv(?:ed|es)|conclud(?:ed|es)|has ended|have ended|ended|called off|cancell?ed|back to normal|returned to normal|all clear)\b/i;

function isResolvedFinding(item: NewsType, raw: NewsRawDataType | null): boolean {
	const validUntil = dateTimeToDate(item.validUntil);
	if (validUntil && validUntil.getTime() < Date.now()) {
		return true;
	}
	const text = `${raw?.title || item.title} ${raw?.summary || item.summary || ''}`;
	return RESOLVED_PATTERN.test(text);
}

const STOP_WORDS = new Set([
	'the',
	'and',
	'for',
	'with',
	'from',
	'that',
	'this',
	'into',
	'after',
	'before',
	'over',
	'near',
	'amid',
	'during',
	'about',
	'have',
	'has',
	'was',
	'were',
	'will',
	'until',
	'been',
	'they',
	'their',
	'there',
	'more',
	'than',
	'some',
	'says',
	'said',
	'new',
	'update',
	'updates',
	'report',
	'reports',
	'reported',
]);

function tokenize(text: string): Set<string> {
	const tokens = text
		.toLowerCase()
		.replace(/[^\p{L}\p{N}\s]/gu, ' ')
		.split(/\s+/)
		.filter((token) => token.length > 3 && !STOP_WORDS.has(token));
	return new Set(tokens);
}

export function toFinding(item: NewsType, latestBatchCutoff: number | null): Finding {
	const raw = parseNewsRawData(item.rawData);
	const publishedAt = dateTimeToDate(item.publishedAt);
	const title = (raw?.title || item.title || '').replace(
		/^\s*\[(?:critical|high|medium|low)\]\s*/i,
		'',
	);
	const summary = raw?.summary || item.summary || '';
	const sourceUrl = raw?.source_url || item.link || '';

	return {
		item,
		raw,
		id: item.id,
		title,
		summary,
		body: raw ? '' : item.content || '',
		category: raw?.category || item.category || '',
		severity: normalizeSeverity(raw?.severity),
		sourceUrl,
		publishedAt,
		eventDateLabel: formatCardDate(item, raw),
		affectedAreas: raw?.affected_areas || [],
		operationalImpact: raw?.operational_impact || '',
		specificAction: raw?.specific_action || null,
		resolved: isResolvedFinding(item, raw),
		tokens: tokenize(`${title} ${summary} ${(raw?.affected_areas || []).join(' ')}`),
		mergedSources: [],
		isNew:
			latestBatchCutoff !== null &&
			publishedAt !== null &&
			publishedAt.getTime() >= latestBatchCutoff,
	};
}

/* -------------------------------------------------------------------------- */
/* Near-duplicate merging + incident threading (§10.13)                        */
/* -------------------------------------------------------------------------- */

const DUPLICATE_THRESHOLD = 0.4;
const THREAD_THRESHOLD = 0.22;
/** Two cards can only be the same story if they land within three days. */
const DUPLICATE_WINDOW_MS = 72 * 60 * 60 * 1000;
/** Keeps token weights stable when a batch only holds a handful of findings. */
const MIN_CORPUS = 12;

type Similarity = (_a: Finding, _b: Finding) => number;

/**
 * Weighted-cosine similarity over title/summary/area tokens, with each token
 * weighted by how rare it is in this batch. Rare names ("flegrei", "pozzuoli",
 * "earthquake") therefore pull an evolving story together, while the words that
 * appear on nearly every card for a given trip ("naples") carry almost no pull.
 */
function createSimilarity(findings: Finding[]): Similarity {
	const total = Math.max(findings.length, MIN_CORPUS);
	const documentFrequency = new Map<string, number>();
	findings.forEach((finding) => {
		finding.tokens.forEach((token) => {
			documentFrequency.set(token, (documentFrequency.get(token) || 0) + 1);
		});
	});

	const weightOf = (token: string) => Math.log(1 + total / (documentFrequency.get(token) || 1));

	const norms = new Map<number, number>();
	const normOf = (finding: Finding) => {
		const cached = norms.get(finding.id);
		if (cached !== undefined) {
			return cached;
		}
		let sum = 0;
		finding.tokens.forEach((token) => {
			const weight = weightOf(token);
			sum += weight * weight;
		});
		const norm = Math.sqrt(sum);
		norms.set(finding.id, norm);
		return norm;
	};

	return (a: Finding, b: Finding) => {
		const normA = normOf(a);
		const normB = normOf(b);
		if (normA === 0 || normB === 0) {
			return 0;
		}
		let dot = 0;
		a.tokens.forEach((token) => {
			if (b.tokens.has(token)) {
				const weight = weightOf(token);
				dot += weight * weight;
			}
		});
		let score = dot / (normA * normB);
		if (a.category && a.category === b.category) {
			score += 0.06;
		}
		return score;
	};
}

/** Collapses cards that describe the same story from different sources. */
export function mergeNearDuplicates(findings: Finding[]): Finding[] {
	const similarity = createSimilarity(findings);
	const kept: Finding[] = [];

	findings.forEach((finding) => {
		const twin = kept.find((candidate) => {
			if (candidate.category !== finding.category) {
				return false;
			}
			const apart = Math.abs(
				(candidate.publishedAt?.getTime() || 0) - (finding.publishedAt?.getTime() || 0),
			);
			if (apart > DUPLICATE_WINDOW_MS) {
				return false;
			}
			return similarity(candidate, finding) >= DUPLICATE_THRESHOLD;
		});
		if (!twin) {
			kept.push(finding);
			return;
		}
		// Keep the newest copy as the canonical card, fold the other one's source in.
		const [canonical, folded] =
			(finding.publishedAt?.getTime() || 0) > (twin.publishedAt?.getTime() || 0)
				? [finding, twin]
				: [twin, finding];

		if (canonical !== twin) {
			kept[kept.indexOf(twin)] = canonical;
			canonical.mergedSources = twin.mergedSources;
		}
		canonical.isNew = canonical.isNew || folded.isNew;
		canonical.resolved = canonical.resolved || folded.resolved;
		if (severityRank(folded.severity) < severityRank(canonical.severity)) {
			canonical.severity = folded.severity;
		}
		const host = sourceHost(folded.sourceUrl);
		const alreadyCited =
			host === sourceHost(canonical.sourceUrl) ||
			canonical.mergedSources.some((source) => source.host === host);
		if (host && !alreadyCited) {
			canonical.mergedSources.push({
				url: folded.sourceUrl,
				host,
				tier: sourceTier(folded.sourceUrl),
			});
		}
	});

	return kept;
}

export type Thread = {
	key: string;
	title: string;
	findings: Finding[];
	severity: SeverityKey;
	latestAt: number;
	resolved: boolean;
	hasNew: boolean;
};

/** Shared significant words become the thread label ("Campi Flegrei earthquake"). */
function threadTitle(findings: Finding[]): string {
	const lead = [...findings].sort((a, b) => {
		const bySeverity = severityRank(a.severity) - severityRank(b.severity);
		if (bySeverity !== 0) {
			return bySeverity;
		}
		return (b.publishedAt?.getTime() || 0) - (a.publishedAt?.getTime() || 0);
	})[0];
	return lead.title;
}

export function buildThreads(findings: Finding[]): Thread[] {
	const similarity = createSimilarity(findings);
	const parent: number[] = findings.map((_finding, index) => index);

	const find = (index: number): number => {
		let root = index;
		while (parent[root] !== root) {
			root = parent[root];
		}
		let cursor = index;
		while (parent[cursor] !== root) {
			const next = parent[cursor];
			parent[cursor] = root;
			cursor = next;
		}
		return root;
	};

	const union = (a: number, b: number) => {
		const rootA = find(a);
		const rootB = find(b);
		if (rootA !== rootB) {
			parent[rootB] = rootA;
		}
	};

	for (let i = 0; i < findings.length; i += 1) {
		for (let j = i + 1; j < findings.length; j += 1) {
			if (similarity(findings[i], findings[j]) >= THREAD_THRESHOLD) {
				union(i, j);
			}
		}
	}

	const groups = new Map<number, Finding[]>();
	findings.forEach((finding, index) => {
		const root = find(index);
		const group = groups.get(root);
		if (group) {
			group.push(finding);
		} else {
			groups.set(root, [finding]);
		}
	});

	const threads: Thread[] = [];
	groups.forEach((group, root) => {
		const ordered = [...group].sort(
			(a, b) => (b.publishedAt?.getTime() || 0) - (a.publishedAt?.getTime() || 0),
		);
		threads.push({
			key: `thread-${root}`,
			title: threadTitle(ordered),
			findings: ordered,
			severity: ordered.reduce<SeverityKey>(
				(worst, finding) =>
					severityRank(finding.severity) < severityRank(worst) ? finding.severity : worst,
				'unknown',
			),
			latestAt: ordered[0]?.publishedAt?.getTime() || 0,
			// A thread is resolved when its newest update says the story closed.
			resolved: ordered[0]?.resolved || false,
			hasNew: ordered.some((finding) => finding.isNew),
		});
	});

	return threads;
}

export type SortKey = 'severity' | 'newest' | 'category';

export function sortThreads(threads: Thread[], sortBy: SortKey): Thread[] {
	const sorted = [...threads];
	if (sortBy === 'newest') {
		sorted.sort((a, b) => b.latestAt - a.latestAt);
		return sorted;
	}
	if (sortBy === 'category') {
		sorted.sort((a, b) => {
			const byCategory = categoryLabel(a.findings[0]?.category).localeCompare(
				categoryLabel(b.findings[0]?.category),
			);
			if (byCategory !== 0) {
				return byCategory;
			}
			return severityRank(a.severity) - severityRank(b.severity);
		});
		return sorted;
	}
	// Severity-first with recency as tiebreaker, resolved threads last (§10.9, §10.14).
	sorted.sort((a, b) => {
		if (a.resolved !== b.resolved) {
			return a.resolved ? 1 : -1;
		}
		const bySeverity = severityRank(a.severity) - severityRank(b.severity);
		if (bySeverity !== 0) {
			return bySeverity;
		}
		return b.latestAt - a.latestAt;
	});
	return sorted;
}

/** Newest publication timestamp across the batch — stands in for "last sweep". */
export function latestPublishedAt(items: NewsType[]): Date | null {
	let latest: Date | null = null;
	items.forEach((item) => {
		const date = dateTimeToDate(item.publishedAt);
		if (date && (!latest || date.getTime() > latest.getTime())) {
			latest = date;
		}
	});
	return latest;
}
